import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    NgZone,
    OnDestroy,
    Output,
    ViewChild,
} from '@angular/core';
import {
    AccountOption,
    AssignmentState,
    AssignmentTarget,
    AccountAssignEvent,
    DocumentFilterCounts,
    DocumentFilterKey,
    DocumentRow,
    DocumentSort,
    DocumentSortKey,
    DocumentSource,
    DocumentUpstreamState,
    DocumentValidity,
    MarkNotRelevantEvent,
    ProposalAcceptEvent,
    SharepointSkipReason,
    SyncReportView,
    SyncRunStatus,
    SyncStartRefusal,
    problemSkipCount,
} from './documents-hub.types';
import {
    AI_STATE_LABEL_KEYS,
    AI_WORKING_KEYS,
    DocumentAiState,
    documentAiStateOf,
    documentKindLabelKeyOf,
    noProposalReasonKeyOf,
    notReadableReasonKeyOf,
    processingStepLabelKeyOf,
    proposalHintKeysOf,
} from '../../presenters/document-ai-state';
import { isoDateLabel } from '../../presenters/document-feedback-keys';
import { SortDirection, ariaSortOf, directionOf, nextSort } from '../sort-header/sort-header.types';

//REV-21367 — closing document hub (Pure View). Contract: e2e-lab/documents-hub.lab.spec.ts.
//
//Dumb by construction (ADR-0009 §10): no store, no HTTP, no filtering, no sorting, no formatting of
//business values. Filter, search and sort are CONTROLLED — they arrive as inputs and leave as
//events; the smart container (REV-21376) owns them.

/** The account picker's width (`.dh-picker__panel`) and its distance to the button and the window edge. */
const PICKER_WIDTH = 420;
const PICKER_GAP = 4;
const PICKER_MARGIN = 8;

interface ProposalCandidate {
    id: string;
    label: string;
    alternative: boolean;
    /** i18n keys of the tooltip: source, then the balance check. Empty = the label as tooltip. */
    hints: string[];
}

/** The three pills, in render order. Always all three — never hidden at count 0 (ADR-0009 §9a). */
const FILTER_KEYS: DocumentFilterKey[] = ['inbox', 'assigned', 'notRelevant'];

const FILTER_LABEL_KEYS: Record<DocumentFilterKey, string> = {
    inbox: 'abcp_documents_filter_inbox',
    assigned: 'abcp_documents_filter_assigned',
    notRelevant: 'abcp_documents_filter_not_relevant',
};

const STATUS_LABEL_KEYS: Record<AssignmentState, string> = {
    open: 'abcp_documents_status_open',
    proposed: 'abcp_documents_status_proposed',
    assigned: 'abcp_documents_status_assigned',
};

/** Die Badge-Varianten des Prototyps (`.bdg.success` / `.info` / `.warn` / `.gray`). */
type BadgeVariant = 'success' | 'info' | 'warn' | 'gray';

const STATUS_VARIANTS: Record<AssignmentState, BadgeVariant> = {
    assigned: 'success',
    proposed: 'info',
    // Offen ist im Prototyp bernstein, nicht grau: eine offene Zuordnung ist eine Pendenz.
    open: 'warn',
};

const SOURCE_LABEL_KEYS: Record<DocumentSource, string> = {
    upload: 'abcp_documents_source_upload',
    sharepoint: 'abcp_documents_source_sharepoint',
    email: 'abcp_documents_source_email',
};

// ─── REV-21964 · U1 — the run report's labels ───────────────────────────────────────────────────
//
// TOTAL maps over closed unions, like every other label map above. Not a string template
// (`'abcp_sync_skip_' + reason`): the compiler checks a record and cannot check a concatenation, so
// a new reason on the backend would render its own identifier at the user instead of failing the
// build. That is the same class of quiet drift `tooLarge` already caused once.

/**
 * REV-21964 · C1-C4 — the upstream marker's tooltip.
 *
 * WARNUNG: a TOTAL map, and it replaced a concatenated key (`'abcp_upstream_state_' + state`) that
 * did not work: the states are camelCase and the keys are snake_case, so `movedOut` asked for
 * `abcp_upstream_state_movedOut`, which exists in no locale — the marker for a file moved out of the
 * connected folder would have rendered its own identifier at the trustee. Nothing throws on a missing
 * key, which is exactly why a concatenation must not be how one is chosen: the compiler checks a
 * record and cannot check a string.
 *
 * `present` is absent because the marker is not rendered at all in that case.
 */
const UPSTREAM_STATE_LABEL_KEYS: Record<Exclude<DocumentUpstreamState, 'present'>, string> = {
    deleted: 'abcp_upstream_state_deleted',
    movedOut: 'abcp_upstream_state_moved_out',
    replaced: 'abcp_upstream_state_replaced',
};

const SKIP_REASON_LABEL_KEYS: Record<SharepointSkipReason, string> = {
    unsupportedType: 'abcp_sync_skip_unsupported_type',
    alreadyImported: 'abcp_sync_skip_already_imported',
    downloadFailed: 'abcp_sync_skip_download_failed',
    rejectedByIngest: 'abcp_sync_skip_rejected_by_ingest',
    listingCapped: 'abcp_sync_skip_listing_capped',
    tooLarge: 'abcp_sync_skip_too_large',
    duplicateContent: 'abcp_sync_skip_duplicate_content',
};

const RUN_STATUS_LABEL_KEYS: Record<SyncRunStatus, string> = {
    pending: 'abcp_sync_run_status_pending',
    running: 'abcp_sync_run_status_running',
    completed: 'abcp_sync_run_status_completed',
    failed: 'abcp_sync_run_status_failed',
};

const REFUSAL_LABEL_KEYS: Record<SyncStartRefusal, string> = {
    closingSealed: 'abcp_sync_refused_closing_sealed',
    alreadyRunning: 'abcp_sync_refused_already_running',
    noBoundFolders: 'abcp_sync_refused_no_bound_folders',
};

/** Sortable columns, in render order. The 7th column (row action) carries no header and no sort. */
const COLUMNS: DocumentSortKey[] = ['filename', 'account', 'confidence', 'addedAt', 'source', 'assignment'];

const COLUMN_LABEL_KEYS: Record<DocumentSortKey, string> = {
    filename: 'abcp_documents_col_filename',
    account: 'abcp_documents_col_account',
    confidence: 'abcp_documents_col_confidence',
    addedAt: 'abcp_documents_col_date',
    source: 'abcp_documents_col_source',
    assignment: 'abcp_documents_col_status',
};

@Component({
    selector: 'abcp-documents-hub',
    templateUrl: './documents-hub.component.html',
    styleUrls: ['./documents-hub.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AbcpDocumentsHubComponent implements OnDestroy {
    /** Already filtered, searched and sorted by the container. The view renders them in order. */
    @Input() documents: DocumentRow[] = [];

    /** All three counts, always rendered — they must sum to the closing's document total. */
    @Input() counts: DocumentFilterCounts = { inbox: 0, assigned: 0, notRelevant: 0 };

    @Input() activeFilter: DocumentFilterKey = 'inbox';

    /** `null` = unsorted; the header then shows the neutral indicator on every column. */
    @Input() sort: DocumentSort | null = null;

    /**
     * The search TERM, not a search field: the input box lives in the parent page's tab strip
     * ("Alle Dokumente durchsuchen…"), so the hub renders none (Entscheid 13.08.2026, Fatma —
     * das kehrt die frühere Festlegung um, der Hub solle es selbst rendern).
     *
     * Kept as a seam together with `searchChange` so the container can move the field in here later
     * without an API change, and so the hub can show "n Treffer für …" if that is ever wanted.
     * `documents` arrives already searched either way.
     */
    @Input() searchTerm = '';

    /** Options of the Konto picker. Read-only here — choosing one only EMITS (REV-21602 persists). */
    @Input() accounts: AccountOption[] = [];

    @Input() syncConnected = false;

    /** Shown next to the sync button, e.g. "bruch-ag.sharepoint.com". */
    @Input() syncTenant: string | null = null;

    /**
     * A SharePoint pull is in flight.
     *
     * The pull walks the bound folders over Graph and downloads a file at a time, so it can take
     * seconds with nothing on screen changing — the rows only appear once the whole import returns
     * and the list is re-read. Without this the button looked inert and invited a second click on top
     * of a running import.
     *
     * The orchestrator has had this state since the import shipped (`syncPending` in the slice); the
     * view simply had no input to receive it, so it was never bound and the feedback never happened.
     */
    @Input() syncPending = false;

    /**
     * REV-21964 · U1 — the LAST RUN's report, or `null` when this closing has never been synced.
     *
     * Read back from the server by the container, so it survives a reload: REV-21376 answered the
     * same question inside the fetch response and kept none of it, which meant "why is my document
     * not there?" had an answer for exactly as long as the tab stayed open.
     */
    @Input() syncReport: SyncReportView | null = null;

    /**
     * R3/R6 — why the last start was REFUSED, or `null` when it was accepted.
     *
     * ⚠️ Not an error, and deliberately not rendered as one: all three refusals are a 200 carrying an
     * answer, and a failure banner would invite the one action that is wrong in every case — pressing
     * the button again.
     *
     * `closingSealed` additionally DISABLES the button (R6). That is not a second source of truth
     * about the closing: a sealed closing does not un-seal while the tab is open, so the server's
     * answer is still the only thing that decided it — the view simply stops asking again.
     */
    @Input() syncRefusal: SyncStartRefusal | null = null;

    @Input() loading = false;
    @Input() error = false;

    /**
     * REV-22302 — is the AI document mapping switched on for this organisation? The host reads it from
     * the same org settings its banner reads. Off, no row is ever shown as "KI liest den Beleg": the
     * banner above the table says no proposal will come, and a row must not claim otherwise.
     */
    @Input() aiActive = true;

    @Output() filterChange = new EventEmitter<DocumentFilterKey>();

    /** Reserved seam — see `searchTerm`. The hub renders no search field, so it never fires today. */
    @Output() searchChange = new EventEmitter<string>();
    /** `null` = back to the hub's own order (newest first). */
    @Output() sortChange = new EventEmitter<DocumentSort | null>();

    /** Row click → the single-document page (REV-21368). */
    @Output() documentOpen = new EventEmitter<string>();

    /** The ONE row action, both directions. There is no delete anywhere in this flow (ADR-0009 §4). */
    @Output() markNotRelevant = new EventEmitter<MarkNotRelevantEvent>();

    /** Assignment controls: rendered and emitted here, persisted by REV-21602. */
    @Output() proposalAccept = new EventEmitter<ProposalAcceptEvent>();
    @Output() proposalReject = new EventEmitter<string>();
    @Output() accountAssign = new EventEmitter<AccountAssignEvent>();

    // ─── #84 — I/O-SKELETT, Template absichtlich noch leer (Contract zuerst rot) ─────────────────
    //
    // Warum diese Mitglieder in EINEM Block stehen: sie tragen N3 aus der signierten Risikoannahme E8
    // (#57/A30). Mit der Auto-Zuweisung ab Confidence ≥ 90 ist die Kompensation K1 („ein Mensch
    // bestaetigt jede Zuordnung") ENTFALLEN; N1 (die Meldung) und N3 (die Auffindbarkeit als Menge)
    // waren ihr Ersatz. N1 ist ENTFERNT (Entscheid Janosch 08.10.2026, REV-22258; Befund in
    // `docs/security/REV-22236.md`, QRM-Sign-off offen) — N3, die Facette, bleibt.
    //
    // Contract: e2e-lab/documents-hub-agent.lab.spec.ts (Sichtbares) und
    // e2e/tests/abcp/documents-proposal-wiring.spec.ts (Verdrahtung).

    /**
     * N3 — „nur automatisch zugewiesene" als CONTROLLED Facette.
     *
     * KEINE vierte Pille, und das ist ein Entscheid, nicht eine Bequemlichkeit: die drei Pillen sind
     * eine echte PARTITION (ADR-0009 §9a) und `documents-hub.lab.spec.ts` nagelt sie mit
     * `toHaveCount(3)` fest. „Maschinell zugewiesen" liegt QUER dazu — jede Agent-Zuordnung ist
     * abgelegt, steht also in `Zugeordnet` — und eine vierte Pille wuerde die Summe der Zaehler
     * gegen die Gesamtzahl brechen.
     */
    @Input() agentOnly = false;

    /**
     * Wie viele der GELADENEN Zeilen maschinell zugewiesen sind — die Zahl am Umschalter.
     *
     * Vom Container gerechnet, wie jede andere Zahl hier: der Hub zaehlt nichts selbst.
     */
    @Input() agentCount = 0;

    @Output() agentOnlyChange = new EventEmitter<boolean>();

    /**
     * Die Belege, deren Bestaetigen/Ablehnen/Ruecknahme GERADE LAEUFT.
     *
     * ⚠️ Sicherheitspruefung (F9/F10) — eine MENGE, kein einzelnes `string | null`: ein einzelner
     * Slot liess einen echten Doppelschreib-Pfad zu (Accept A → Accept B → Accept A: der dritte
     * Klick faende A "nicht mehr gesperrt", obwohl As Schreibvorgang noch laeuft). Jede Zeile
     * prueft nur ihre EIGENE Id gegen diese Menge.
     *
     * Damit der Knopf nicht HAENGT: er zeigt seinen Lauf und nimmt keinen zweiten Klick an. Ohne
     * das sieht ein Schreibvorgang, der eine Sekunde braucht, wie ein toter Knopf aus.
     */
    @Input() proposalPendingIds: string[] = [];
    /**
     * REV-22366 — rows with a write running (Nicht relevant, choosing an account): faded, not
     * clickable, a spinner for the eye, until the list shows the result.
     */
    @Input() pendingRowIds: string[] = [];
    /** REV-22366 — files being uploaded: a placeholder row each in Eingang, until the list holds them. */
    @Input() uploadingFilenames: string[] = [];

    /**
     * Die Belege, deren letztes Bestaetigen/Ablehnen/Ruecknahme FEHLGESCHLAGEN ist.
     *
     * Eigene Eingabe und nicht `error`: `error` ist „die Liste konnte nicht geladen werden" und
     * verdeckt die Tabelle. Ein gescheiterter Schreibvorgang darf die Liste nicht wegnehmen, er
     * muss AN DER ZEILE stehen und den Knopf wieder benutzbar lassen. Eine MENGE aus demselben
     * Grund wie oben: der Fehler einer Zeile darf den einer anderen nicht ueberschreiben.
     */
    @Input() proposalFailedIds: string[] = [];

    /** The upload modal itself is REV-21363, opened by REV-21376 — the hub only asks. */
    @Output() uploadRequested = new EventEmitter<void>();
    @Output() filesDropped = new EventEmitter<File[]>();

    @Output() syncNowRequested = new EventEmitter<void>();
    @Output() syncSettingsRequested = new EventEmitter<void>();

    readonly filterKeys = FILTER_KEYS;
    readonly columns = COLUMNS;
    readonly skeletonRows = [1, 2, 3, 4, 5, 6];

    /**
     * Angular's `date` pipe with the separator as a quoted literal — the prototype's
     * "14.04.2026 | 01:51". A raw `|` in a template binding would parse as a pipe.
     */
    readonly dateFormat = "dd.MM.yyyy '|' HH:mm";

    /** Drag-hover is transient DOM state, like `:hover` — the view owns it, nothing upstream cares. */
    isDragOver = false;

    /** Same category: whether the sync dropdown is open is presentation, not application state. */
    isSyncMenuOpen = false;

    /**
     * Whether the run report is expanded. Transient like the menu above — and collapsed by default,
     * because the normal outcome of a sync is "nothing to read".
     *
     * The COUNTS and the truncation notice are outside this toggle; only the per-file list folds.
     * Hiding the counts behind a click would make "3 skipped" something the user has to go looking
     * for, and a number nobody sees is the same as no number.
     */
    isSyncReportOpen = false;

    readonly skipReasonLabelKeys = SKIP_REASON_LABEL_KEYS;
    readonly runStatusLabelKeys = RUN_STATUS_LABEL_KEYS;
    readonly refusalLabelKeys = REFUSAL_LABEL_KEYS;
    readonly aiWorkingKeys = AI_WORKING_KEYS;
    readonly aiStateLabelKeys = AI_STATE_LABEL_KEYS;

    // --- Konto-Wähler ---------------------------------------------------------------------------
    // Which row's picker is open, how far it has got, and what has been chosen so far. All four are
    // transient widget state of the same kind as `isDragOver`: nothing upstream owns "a dropdown is
    // half-open", and the ASSIGNMENT itself is only ever emitted, never stored here.
    //
    // The picker filters its own option list. That is not a breach of "the view never filters" —
    // that rule is about the DOCUMENT list, which arrives ready from the container. Searching inside
    // one's own dropdown is widget-internal behaviour, the same as any off-the-shelf dropdown does.
    openPickerDocumentId: string | null = null;
    pickerStep: 'account' | 'validity' = 'account';
    pickerSearch = '';
    pickerTarget: AssignmentTarget | null = null;

    /**
     * Where the open picker sits, in window coordinates. The panel lives in the app's global flyout
     * layer (`hamFlyout`), not in its row: the table scrolls inside its frame, and anything inside a
     * scroll box is cut off at its edge whatever its z-index — which is why the table used to need a
     * minimum height. Fixed to the window, the panel reaches past the table.
     */
    pickerPosition: { top: number; left: number; right: number } | null = null;
    private pickerTrigger: HTMLElement | null = null;
    private pickerPanel: HTMLElement | null = null;
    private pickerResize: ResizeObserver | null = null;

    /** Scrolling or resizing moves the button; the panel follows. A scroll INSIDE the panel does not. */
    private readonly onPickerAnchorMoved = (event: Event): void => {
        if (event.target instanceof Node && this.pickerPanel?.contains(event.target)) return;
        this.placePicker();
    };

    constructor(
        private readonly cdr: ChangeDetectorRef,
        private readonly zone: NgZone,
    ) {}

    /** The panel exists: watch its size, which changes with the step and the search. */
    @ViewChild('pickerPanel', { read: ElementRef })
    set pickerPanelRef(ref: ElementRef<HTMLElement> | undefined) {
        this.pickerResize?.disconnect();
        this.pickerResize = null;
        this.pickerPanel = ref?.nativeElement ?? null;
        if (this.pickerPanel !== null && typeof ResizeObserver !== 'undefined') {
            this.pickerResize = new ResizeObserver(() => this.zone.run(() => this.placePicker()));
            this.pickerResize.observe(this.pickerPanel);
        }
    }

    get isEmpty(): boolean {
        return this.documents.length === 0 && this.uploadRows.length === 0;
    }

    /** REV-22366 — uploads land in Eingang, so only that tab shows their placeholders. */
    get uploadRows(): string[] {
        return this.activeFilter === 'inbox' ? this.uploadingFilenames : [];
    }

    /**
     * Placeholders only while there is nothing to show yet. A reload keeps the rows it has until the new
     * ones arrive: placeholders over old rows read as two states at once, and swapping the rows for
     * placeholders on every refresh (a poll, a reload after a write) makes the table flash.
     */
    get showSkeleton(): boolean {
        return this.loading && this.documents.length === 0 && this.uploadRows.length === 0;
    }

    /** F8 — the Belegtyp column's tooltip hint. Only that column explains itself on hover. */
    columnHintKey(key: DocumentSortKey): string | null {
        return key === 'confidence' ? 'abcp_documents_col_confidence_hint' : null;
    }

    /** The six sortable columns plus the unlabelled action column. */
    get columnCount(): number {
        return this.columns.length + 1;
    }

    /**
     * The picker's visible options. Matches number OR name OR account group — the group is not a
     * column, so this is the only way a user who thinks in groups can find the account.
     */
    get filteredAccounts(): AccountOption[] {
        const term = this.pickerSearch.trim().toLowerCase();
        if (!term) return this.accounts;
        return this.accounts.filter(
            (account) =>
                account.number.toLowerCase().includes(term) ||
                account.name.toLowerCase().includes(term) ||
                (account.group ?? '').toLowerCase().includes(term)
        );
    }

    /** The Projekt-Akte row is pinned to the top, and only while nothing is being searched. */
    get isProjectFileOptionVisible(): boolean {
        return this.pickerSearch.trim().length === 0;
    }

    get isProjectFileTarget(): boolean {
        return this.pickerTarget?.kind === 'projectFile';
    }

    /** Header of the validity step: the account just picked, or '' for the Projekt-Akte (i18n'd in the template). */
    get pickerTargetLabel(): string {
        const target = this.pickerTarget;
        if (target?.kind !== 'account') return '';
        const account = this.accounts.find((option) => option.id === target.accountId);
        return account ? `${account.number} ${account.name}` : '';
    }

    isPickerOpen(row: DocumentRow): boolean {
        return this.openPickerDocumentId === row.id;
    }

    filterLabelKey(key: DocumentFilterKey): string {
        return FILTER_LABEL_KEYS[key];
    }

    filterCount(key: DocumentFilterKey): number {
        return this.counts[key];
    }

    columnLabelKey(key: DocumentSortKey): string {
        return COLUMN_LABEL_KEYS[key];
    }

    statusLabelKey(state: AssignmentState): string {
        return STATUS_LABEL_KEYS[state];
    }

    statusVariant(state: AssignmentState): BadgeVariant {
        return STATUS_VARIANTS[state];
    }

    sourceLabelKey(source: DocumentSource): string {
        return SOURCE_LABEL_KEYS[source];
    }

    /**
     * REV-22302 — the Belegtyp column shows the RECOGNISED KIND as a neutral label, never the model's
     * self-reported confidence: that number read as certainty about the account and was "100 %" on
     * nearly every row. `''` (→ `—`) for an unknown or absent kind, never the identifier.
     */
    kindLabelKey(row: DocumentRow): string {
        return documentKindLabelKeyOf(row.kind);
    }

    /**
     * REV-22302 — what the account column says about the AI for this row, from the row's own fields
     * (`presenters/document-ai-state.ts`). Read at render time against the clock: the "working" claim
     * expires a quarter hour after arrival, and the poll behind this view re-renders the rows every
     * few seconds while any of them is still working, so the expiry is seen without a timer here.
     */
    aiState(row: DocumentRow): DocumentAiState | null {
        return documentAiStateOf(row, Date.now(), this.aiActive);
    }

    /**
     * The picker is offered wherever nothing is filed and no proposal stands — EXCEPT while the AI is
     * still working on the row: the indicator takes the column then, and the picker returns with the
     * outcome (or, at the latest, when the working window closes).
     */
    showsPicker(row: DocumentRow): boolean {
        return this.isUnfiled(row) && this.aiState(row) !== 'working';
    }

    /** The compact picker sits beside a note ("Kein Vorschlag", "Nicht lesbar"); the plain one fills the column. */
    isPickerCompact(row: DocumentRow): boolean {
        return this.aiNoteLabelKey(row) !== '';
    }

    /** `''` when the row carries no note — the plain picker alone is drawn then. */
    aiNoteLabelKey(row: DocumentRow): string {
        switch (this.aiState(row)) {
            case 'noProposal':
                return AI_STATE_LABEL_KEYS.noProposal;
            case 'notReadable':
                return AI_STATE_LABEL_KEYS.notReadable;
            default:
                return '';
        }
    }

    /** The note's info tooltip: WHY there is no proposal, or why the file could not be read. */
    aiNoteReasonKey(row: DocumentRow): string {
        switch (this.aiState(row)) {
            case 'noProposal':
                return noProposalReasonKeyOf(row);
            case 'notReadable':
                return notReadableReasonKeyOf(row);
            default:
                return '';
        }
    }

    /** Confirmed = an account is on the document. Projekt-Akte is filed but has no account. */
    isConfirmed(row: DocumentRow): boolean {
        return row.assignment === 'assigned' && row.filing === 'account' && !!row.account;
    }

    isProposal(row: DocumentRow): boolean {
        return row.assignment === 'proposed' && !!row.account;
    }

    /** REV-22302 — one chip per candidate: the proposed account, then the other strand's pick. */
    proposalCandidates(row: DocumentRow): ProposalCandidate[] {
        if (!row.account) return [];
        const candidates = [
            { id: row.account.id, label: row.account.label, alternative: false, hints: proposalHintKeysOf(row, false) },
        ];
        if (row.alternativeAccountId) {
            const option = this.accounts.find((account) => account.id === row.alternativeAccountId);
            candidates.push({
                id: row.alternativeAccountId,
                label: option ? `${option.number} ${option.name}` : row.alternativeAccountId,
                alternative: true,
                hints: proposalHintKeysOf(row, true),
            });
        }
        return candidates;
    }

    processingStepKey(row: DocumentRow): string {
        return processingStepLabelKeyOf(row.processingStep);
    }

    isProjectFile(row: DocumentRow): boolean {
        return row.filing === 'projectFile';
    }

    /** The picker shows only where nothing is filed yet and nothing is proposed. */
    isUnfiled(row: DocumentRow): boolean {
        return !this.isConfirmed(row) && !this.isProposal(row) && !this.isProjectFile(row);
    }

    /** `aria-sort` for a header: only the sorted column claims a direction. */
    ariaSort(key: DocumentSortKey): 'ascending' | 'descending' | 'none' {
        return ariaSortOf(this.sort, key);
    }

    sortDirection(key: DocumentSortKey): SortDirection | null {
        return directionOf(this.sort, key);
    }

    trackById(_index: number, item: { id: string }): string {
        return item.id;
    }

    onFilterClick(key: DocumentFilterKey): void {
        this.filterChange.emit(key);
    }

    /** A new column starts ascending, the sorted one turns descending, a third click un-sorts. */
    onSortClick(key: DocumentSortKey): void {
        this.sortChange.emit(nextSort(this.sort, key));
    }

    onRowClick(id: string): void {
        this.documentOpen.emit(id);
    }

    isRowPending(row: DocumentRow): boolean {
        return this.pendingRowIds.includes(row.id);
    }

    onNotRelevantToggle(row: DocumentRow): void {
        this.markNotRelevant.emit({ documentId: row.id, notRelevant: !row.notRelevant });
    }

    certaintyIcon(row: { readonly proposalCertainty?: string | null }, alternative: boolean): string {
        if (alternative || !row.proposalCertainty) {
            return 'sparkles';
        }
        return row.proposalCertainty === 'HIGH' ? 'shield-check' : 'triangle-alert';
    }

    onProposalAccept(documentId: string, accountId: string): void {
        this.proposalAccept.emit({ documentId, accountId });
    }

    /** ✗ = none of the candidates: reject, and open the picker right away so the user picks the account. */
    onProposalReject(row: DocumentRow, anchor?: HTMLElement): void {
        this.proposalReject.emit(row.id);
        if (!this.isPickerOpen(row)) this.onPickerToggle(row, anchor);
    }

    /** N3 — the facet is CONTROLLED, exactly like the pills: it renders `agentOnly` and never flips it. */
    onAgentFilterToggle(): void {
        this.agentOnlyChange.emit(!this.agentOnly);
    }

    /** Opening a picker closes any other — two open panels would overlap and confuse the target. */
    onPickerToggle(row: DocumentRow, trigger?: HTMLElement): void {
        if (this.isPickerOpen(row)) {
            this.closePicker();
            return;
        }
        this.openPickerDocumentId = row.id;
        this.resetPicker();
        this.pickerTrigger = trigger ?? null;
        this.placePicker();
        window.addEventListener('scroll', this.onPickerAnchorMoved, true);
        window.addEventListener('resize', this.onPickerAnchorMoved);
    }

    onPickerSearch(term: string): void {
        this.pickerSearch = term;
    }

    /** Step 1 → step 2. Nothing is emitted yet: the validity question is part of the same gesture. */
    onAccountPick(accountId: string): void {
        this.pickerTarget = { kind: 'account', accountId };
        this.pickerStep = 'validity';
    }

    onProjectFilePick(): void {
        this.pickerTarget = { kind: 'projectFile' };
        this.pickerStep = 'validity';
    }

    /** Step 2 completes the gesture and is the ONLY place the assignment leaves the view. */
    onValidityPick(validity: DocumentValidity): void {
        const documentId = this.openPickerDocumentId;
        const target = this.pickerTarget;
        if (documentId && target) this.accountAssign.emit({ documentId, target, validity });
        this.closePicker();
    }

    onPickerBack(): void {
        this.pickerStep = 'account';
        this.pickerTarget = null;
    }

    closePicker(): void {
        this.openPickerDocumentId = null;
        this.resetPicker();
        this.pickerTrigger = null;
        this.pickerPosition = null;
        window.removeEventListener('scroll', this.onPickerAnchorMoved, true);
        window.removeEventListener('resize', this.onPickerAnchorMoved);
    }

    ngOnDestroy(): void {
        this.closePicker();
        this.pickerResize?.disconnect();
    }

    /**
     * Below the button; above it when it would leave the window there and fits above. Kept inside the
     * window horizontally, so a picker opened near the right edge does not hang off it.
     */
    private placePicker(): void {
        const trigger = this.pickerTrigger;
        if (trigger === null || this.openPickerDocumentId === null) return;

        const rect = trigger.getBoundingClientRect();
        const height = this.pickerPanel?.offsetHeight ?? 0;
        const below = rect.bottom + PICKER_GAP;
        const above = rect.top - PICKER_GAP - height;
        const top =
            height > 0 && below + height > window.innerHeight - PICKER_MARGIN && above >= PICKER_MARGIN ? above : below;
        const left = Math.max(PICKER_MARGIN, Math.min(rect.left, window.innerWidth - PICKER_WIDTH - PICKER_MARGIN));

        this.pickerPosition = { top, left, right: window.innerWidth - left - PICKER_WIDTH };
        this.cdr.markForCheck();
    }

    /** A click anywhere outside closes the panel — the picker stops propagation on its own subtree. */
    @HostListener('document:click')
    onDocumentClick(): void {
        if (this.openPickerDocumentId !== null) this.closePicker();
        if (this.isSyncMenuOpen) this.isSyncMenuOpen = false;
    }

    @HostListener('document:keydown.escape')
    onEscape(): void {
        this.closePicker();
        this.isSyncMenuOpen = false;
    }

    private resetPicker(): void {
        this.pickerStep = 'account';
        this.pickerSearch = '';
        this.pickerTarget = null;
    }

    onUploadClick(): void {
        this.uploadRequested.emit();
    }

    /**
     * The marker's tooltip for one row. `''` for `present`/absent, which the template never asks for
     * — the marker itself is behind the same condition.
     */
    upstreamStateLabelKey(state: DocumentUpstreamState | undefined): string {
        return state == null || state === 'present' ? '' : UPSTREAM_STATE_LABEL_KEYS[state];
    }

    /** The two dates of a date warning, as the user reads them. */
    dateWarningParams(warning: { balanceDate: string; balanceSheetDate: string }): {
        balanceDate: string;
        balanceSheetDate: string;
    } {
        return { balanceDate: isoDateLabel(warning.balanceDate), balanceSheetDate: isoDateLabel(warning.balanceSheetDate) };
    }

    onSyncMenuToggle(): void {
        this.isSyncMenuOpen = !this.isSyncMenuOpen;
    }

    onSyncNowClick(): void {
        this.isSyncMenuOpen = false;
        // Swallowed while one is running. The effect already ignores a second request
        // (`exhaustMap`), so this is not the guard — it is what stops the button LOOKING like it
        // accepted a click it is going to drop.
        if (this.syncPending || this.syncBlocked) {
            return;
        }
        // REV-22096 — with NO connection, "synchronise" is a request to sign in. A pull against a
        // disconnected or expired connection cannot do anything, and the only place that offers the
        // Microsoft login is the settings shell behind `syncSettingsRequested` — so the main button
        // opens it instead of asking for a run the server would have to refuse.
        if (!this.syncConnected) {
            this.syncSettingsRequested.emit();
            return;
        }
        this.syncNowRequested.emit();
    }

    /**
     * R6 — a sealed closing takes no further documents, so the button stops offering.
     *
     * The SERVER is still what decides this; the view only remembers the answer it was given. There
     * is no second reading of "is this closing sealed?" here, which is what would let the button and
     * the backend disagree.
     */
    get syncBlocked(): boolean {
        return this.syncRefusal === 'closingSealed';
    }

    /** `true` once there is anything to report — a run exists, whatever it found. */
    get hasSyncReport(): boolean {
        return this.syncReport !== null;
    }

    /** Files of the last run that did not come in because of a real problem; shown in orange. */
    get syncProblemFiles(): number {
        return this.syncReport ? problemSkipCount(this.syncReport.skips) : 0;
    }

    onSyncReportToggle(): void {
        this.isSyncReportOpen = !this.isSyncReportOpen;
    }

    onSyncSettingsClick(): void {
        this.isSyncMenuOpen = false;
        this.syncSettingsRequested.emit();
    }

    onDragOver(event: DragEvent): void {
        event.preventDefault();
        this.isDragOver = true;
    }

    onDragLeave(): void {
        this.isDragOver = false;
    }

    onDrop(event: DragEvent): void {
        event.preventDefault();
        this.isDragOver = false;
        const files = Array.from(event.dataTransfer?.files ?? []);
        if (files.length > 0) this.filesDropped.emit(files);
    }
}
